import {
  contractUnrealizedPnl,
  EMPTY_VALUE,
  formatContractQty,
  formatPrice,
  formatRoePercent,
  formatSettleAmount,
  formatSignedSettleAmount,
  formatUsd,
  isInversePair,
  notionalValue,
  positionValueBase,
  roePercent,
  settleCurrency,
  settleDecimals,
  snapZero,
} from '@outpoll/utils';
import type { BestQuote, FuturesPair, PositionDto } from '@/lib/api/futures/client';
import type {
  FuturesMarginMode,
  FuturesPositionHistory,
  FuturesSide,
} from '@/lib/api/history/profile-types';
import type { Coin } from '@/lib/api/transaction/models';

const POSITIVE_CLASS = 'text-v2-btn-premium-green-light';
const NEGATIVE_CLASS = 'text-v2-btn-premium-red-light';

export function sideClass(side: FuturesSide): string {
  return side === 'LONG' ? POSITIVE_CLASS : NEGATIVE_CLASS;
}

export function signClass(value: number): string {
  return value >= 0 ? POSITIVE_CLASS : NEGATIVE_CLASS;
}

export function sideKey(side: FuturesSide): 'buyLong' | 'sellShort' {
  return side === 'LONG' ? 'buyLong' : 'sellShort';
}

/** History direction label key (`trade.direction`). */
export function closeDirectionKey(side: FuturesSide): 'closeLong' | 'closeShort' {
  return side === 'LONG' ? 'closeLong' : 'closeShort';
}

export function marginModeKey(mode: FuturesMarginMode): 'isolated' | 'cross' {
  return mode === 'ISOLATED' ? 'isolated' : 'cross';
}

const QUOTE_SUFFIXES = ['USDT', 'USDC', 'USD'];

export interface Contract {
  symbol: string;
  /** Terminal path, locale-less. */
  path: string;
  inverse: boolean;
  /** Size unit: base (linear) / quote contracts (inverse). */
  sizeUnit: string;
  /** Settle coin — margin, PnL and position value. */
  settle: string;
  settleDecimals: number;
  pair?: FuturesPair;
}

/**
 * Pair details by id — the positions DTO comes without a symbol. Falls back to
 * the DTO symbol (linear) while the pairs list loads.
 */
export function resolveContract(
  pairs: FuturesPair[] | undefined,
  coins: Coin[],
  pairId: number,
  symbol: string,
): Contract {
  const pair = pairs?.find((p) => p.id === pairId);
  if (pair) {
    const inverse = isInversePair(pair);
    return {
      symbol: pair.symbol,
      path: pair.path,
      inverse,
      sizeUnit: inverse ? pair.quoteCurrency : pair.baseCurrency,
      settle: settleCurrency(pair),
      settleDecimals: settleDecimals(pair, coins),
      pair,
    };
  }
  const quote = QUOTE_SUFFIXES.find((q) => symbol.endsWith(q)) ?? 'USDT';
  const fallbackPair = { contractType: '', lotSize: 0, baseCurrency: '', quoteCurrency: quote };
  return {
    symbol,
    path: symbol ? `/trade/${quote.toLowerCase()}/${symbol}` : '/trade',
    inverse: false,
    sizeUnit: symbol.replace(new RegExp(`${quote}$`), ''),
    settle: quote,
    settleDecimals: settleDecimals(fallbackPair, coins),
  };
}

export function fmtQty(value: number, contract: Contract): string {
  return `${formatContractQty(value, contract.inverse, contract.pair?.lotSize)} ${contract.sizeUnit}`;
}

/** Settle-coin amount (margin, value, PnL) at the settle coin's precision. */
export function fmtAmount(value: number, contract: Contract): string {
  const decimals = contract.settleDecimals;
  return `${formatSettleAmount(snapZero(value, decimals), contract.inverse, decimals)} ${contract.settle}`;
}

/** A signed settle-coin amount (PnL): "+12.40 USDT", "-0.0012 BTC". */
export function fmtSignedAmount(value: number, contract: Contract): string {
  const amount = formatSignedSettleAmount(value, contract.inverse, contract.settleDecimals);
  return `${amount} ${contract.settle}`;
}

/** Price at the pair's tick precision; "—" when the backend sent none (0 / null). */
export function fmtPrice(value: number, contract: Contract): string {
  if (!value) return EMPTY_VALUE;
  return formatPrice(value, contract.pair?.tickSize);
}

/** "≈ 0.04 USD" (design); hidden for inverse — those settle in the coin. */
export function fmtUsdApprox(value: number, contract: Contract): string | undefined {
  if (contract.inverse) return undefined;
  return `≈ ${formatUsd(snapZero(value))} USD`;
}

/** uPnL at the close-side quote (long → bid, short → ask), as the terminal shows it; the REST snapshot until it ticks. */
export function liveUnrealizedPnl(
  position: PositionDto,
  quote: BestQuote | undefined,
  contract: Contract,
): number {
  const side = position.s === 'LONG' ? 'long' : 'short';
  const price = side === 'long' ? quote?.bid : quote?.ask;
  if (price == null) return position.upn;
  return contractUnrealizedPnl(contract.inverse, side, position.e, price, Math.abs(position.sz));
}

export function fmtRoe(pnl: number, margin: number): string {
  return formatRoePercent(roePercent(pnl, margin));
}

/** A closed position's margin, implied from its entry value and leverage. */
export function closedPositionMargin(position: FuturesPositionHistory, contract: Contract): number {
  const { closedQty, entryPrice, leverage } = position;
  return leverage ? positionValue(closedQty, entryPrice, contract) / leverage : 0;
}

/** A closed position's ROE %, as its history row and share card show it. */
export function closedPositionRoe(position: FuturesPositionHistory, contract: Contract): number {
  return roePercent(position.realizedPnl, closedPositionMargin(position, contract));
}

/** Position value in the settle coin: q × price (linear), q / price (inverse). */
export function positionValue(qty: number, price: number, contract: Contract): number {
  return contract.inverse ? positionValueBase(qty, price) : notionalValue(price, qty);
}

/**
 * Mark price implied by the snapshot uPnL, so the share card matches the table.
 * Linear: uPnL = q × (mark − entry); inverse: uPnL = q × (1/entry − 1/mark).
 */
export function impliedMarkPrice(
  isLong: boolean,
  entry: number,
  size: number,
  upnl: number,
  contract: Contract,
): number {
  if (!size || !entry) return entry;
  const signed = isLong ? upnl : -upnl;
  if (!contract.inverse) return entry + signed / size;
  const inverseMark = 1 / entry - signed / size;
  return inverseMark > 0 ? 1 / inverseMark : entry;
}
